import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  APIProvider, Map, Marker, InfoWindow,
  useMarkerRef
} from '@vis.gl/react-google-maps';
import './map.css';

/**
 * Google Map component for single location display
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.center - Latitude and longitude coordinates
 * @param {number} props.zoom - Map zoom level
 * @param {Function} props.onError - Error callback function
 * @param {string} props.title - Location title for the info window
 * @param {string} props.address - Location address for the info window
 * @returns {JSX.Element} - Rendered component
 */
export const GMap = ({ center, zoom, onError, title, address }) => {
  // Get API key from environment variables instead of importing directly
  const MAPS_API_KEY = process.env.REACT_APP_GMAP_KEY;
  const [markerRef] = useMarkerRef();
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  // Default center for fallback
  const defaultCenter = { lat: 43.4516, lng: -80.4925 }; // Kitchener coordinates

  // Handle window resize
  const handleResize = useCallback(() => {
    if (mapInstance) {
      mapInstance.resize();
    }
  }, [mapInstance]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Initialize map
  useEffect(() => {
    if (!MAPS_API_KEY) {
      const error = new Error('Google Maps API key is missing');
      setMapError(error.message);
      onError?.(error);
      return;
    }

    if (center && (typeof center.lat !== 'number' || typeof center.lng !== 'number')) {
      const error = new Error('Invalid coordinates provided');
      setMapError(error.message);
      onError?.(error);
      return;
    }

    setIsLoading(false);
  }, [center, onError]);

  const handleMapLoad = (map) => {
    setMapInstance(map);
    setIsLoading(false);
  };

  if (mapError) {
    return (
      <div className="map-error-container">
        <p>Error loading map: {mapError}</p>
      </div>
    );
  }

  return (
    <div className="map-container">
      {isLoading && (
        <div className="map-loading">
          <p>Loading map...</p>
        </div>
      )}
      <APIProvider apiKey={MAPS_API_KEY}>
        <Map
          mapId="property-map"
          style={{ width: '100%', height: '100%' }}
          defaultCenter={center || defaultCenter}
          defaultZoom={zoom || 12}
          gestureHandling="cooperative"
          disableDefaultUI={false}
          streetViewControl={true}
          zoomControl={true}
          mapTypeControl={true}
          onLoad={handleMapLoad}
          onError={(error) => {
            setMapError(error.message);
            onError?.(error);
          }}
          options={{
            scrollwheel: true,
            fullscreenControl: true,
            mapTypeControlOptions: {
              position: 3, // TOP_RIGHT
              style: 2 // DROPDOWN_MENU
            }
          }}
        >
          <Marker
            ref={markerRef}
            position={center || defaultCenter}
            onClick={() => setShowInfoWindow(true)}
          />
          {showInfoWindow && (
            <InfoWindow
              position={center || defaultCenter}
              onCloseClick={() => setShowInfoWindow(false)}
            >
              <div className="info-window-content">
                <h5>{title}</h5>
                <p>{address}</p>
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};